import APIService from "./APIService";
import { getCookie } from "../utils/CookieUtil";

/**
 * Service for handling RecentSimulations API operations
 *
 * Expected RecentActivityDTO structure (based on backend):
 * {
 *   activityId: Long,
 *   userId: Long,
 *   email: String,
 *   phone: String,
 *   channelType: String (email, sms, voice, social-media),
 *   activityType: String,
 *   messageId: Long,
 *   subject: String,
 *   contentSnippet: String,
 *   status: String,
 *   createdAt: LocalDateTime,
 *   sentAt: LocalDateTime,
 *   deliveredAt: LocalDateTime,
 *   senderId: String,
 *   ipAddress: String,
 *   userAgent: String,
 *   externalId: String,
 *   trackingId: String,
 *   properties: String (JSON)
 * }
 */
const RecentSimulationsAPI = {
  /**
   * Write a message activity to RecentSimulations backend
   * @param {Object} messageData - The message data that was sent
   * @param {string} [messageData.messageId] - Unique message identifier
   * @param {string} [messageData.phone] - Phone number for SMS/voice simulations
   * @param {string} [messageData.subject] - Message subject
   * @param {string} [messageData.content] - Message content
   * @param {string} [messageData.recipient] - Message recipient
   * @param {string} [messageData.communicationContext] - Activity type/context
   * @param {string} [messageData.sophisticationLevel] - Message sophistication level
   * @param {string} [messageData.status] - Message status (sent, delivered, failed)
   * @param {string} [messageData.senderId] - ID of the sender
   * @param {string} [messageData.ipAddress] - IP address of the sender
   * @param {string} [messageData.userAgent] - User agent string
   * @param {string} [messageData.externalId] - External system identifier
   * @param {string} [messageData.trackingId] - Tracking identifier
   * @param {Object} [messageData.properties] - Additional properties (will be JSON stringified)
   * @param {string} [messageData.timestamp] - Custom timestamp
   * @param {string} [messageData.sentAt] - When message was sent
   * @param {string} [messageData.deliveredAt] - When message was delivered
   * @param {string} channelType - The communication channel (email, sms, voice, social-media)
   */
  writeActivity: async (messageData, channelType) => {
    try {
      // Get current user from cookie
      const userUuid = getCookie("user");
      if (!userUuid) {
        console.warn(
          "[RecentSimulationsAPI] No user selected, skipping recent activity write"
        );
        return;
      }

      // Get user ID from UUID using cookie-based authentication
      const userResponse = await APIService.get(`/api/v1/base/users/by-uuid`);
      if (!userResponse || !userResponse.userId) {
        console.warn(
          "[RecentSimulationsAPI] Unable to retrieve user information for recent activity"
        );
        return;
      }
      const userId = userResponse.userId;

      // Get current timestamp for timing fields
      const currentTimestamp = new Date().toISOString();

      // Prepare properties object to store additional frontend data
      const properties = {
        recipient: getRecipient(messageData, channelType),
        sophisticationLevel: messageData.sophisticationLevel || "Basic",
        originalContent: getMessageContent(messageData),
        ...(messageData.properties || {}), // Include any additional properties passed in
      };

      // Prepare the activity data to match backend RecentActivityDTO structure
      const activityData = {
        userId: userId,
        email: String(
          userResponse.email ||
            userResponse.userEmail ||
            (typeof userResponse === "object" ? userResponse.email : null) ||
            "unknown@example.com"
        ),
        phone: messageData.phone || userResponse.phone || null,
        channelType: String(channelType.toLowerCase()),
        activityType: String(
          messageData.communicationContext ||
            messageData.activityType ||
            "simulation"
        ),
        messageId: (() => {
          // Handle different messageId types and convert to Long
          if (messageData.messageId) {
            if (typeof messageData.messageId === "number") {
              return messageData.messageId;
            }
            if (typeof messageData.messageId === "string") {
              const extracted = messageData.messageId.replace(/\D/g, "");
              return extracted ? parseInt(extracted) : Date.now();
            }
          }
          return Date.now(); // Fallback to current timestamp
        })(),
        subject: String(messageData.subject || getDefaultSubject(channelType)),
        contentSnippet: String(
          getMessageContent(messageData).substring(0, 500)
        ), // Limit content snippet
        status: String(messageData.status || "sent"),
        createdAt: String(currentTimestamp),
        sentAt: String(messageData.sentAt || currentTimestamp),
        deliveredAt: messageData.deliveredAt
          ? String(messageData.deliveredAt)
          : null,
        senderId: String(messageData.senderId || "system"),
        ipAddress: messageData.ipAddress ? String(messageData.ipAddress) : null,
        userAgent: messageData.userAgent
          ? String(messageData.userAgent)
          : typeof navigator !== "undefined" && navigator.userAgent
          ? String(navigator.userAgent)
          : null,
        externalId: messageData.externalId
          ? String(messageData.externalId)
          : null,
        trackingId: String(
          messageData.trackingId ||
            `track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        ),
        properties: JSON.stringify(properties),
      };

      console.log(
        `[RecentSimulationsAPI] Writing ${channelType} activity to recent simulations:`,
        activityData
      );

      // Debug: Log the types of key fields to help identify issues
      console.log("[RecentSimulationsAPI] Field types:", {
        userId: typeof activityData.userId,
        email: typeof activityData.email,
        properties: typeof activityData.properties,
        messageId: typeof activityData.messageId,
      });

      // Write to the recent simulations API
      await APIService.post(
        "/api/v1/simulation/communication-simulations",
        activityData
      );

      console.log(
        `[RecentSimulationsAPI] Successfully wrote ${channelType} activity to recent simulations`
      );
    } catch (error) {
      console.error(
        `[RecentSimulationsAPI] Error writing ${channelType} activity to recent simulations:`,
        error
      );
      throw error; // Re-throw so the caller can handle it
    }
  },
  /**
   * Get recent simulations for a user
   * If userUuid is provided, uses admin endpoint (requires authorization)
   * If userUuid is not provided, uses self-access endpoint with authentication context
   * @param {string} [userUuid] - Optional UUID of user to fetch simulations for (admin only)
   * @returns {Promise<Array>} Array of CommunicationSimulationDTO objects
   */
  getSimulationsForUser: async (userUuid = null) => {
    try {
      const endpoint = userUuid
        ? `/api/v1/simulation/communication-simulations/user/${userUuid}`
        : `/api/v1/simulation/communication-simulations/user`;

      console.log(
        `[RecentSimulationsAPI] Fetching simulations using endpoint: ${endpoint}`
      );
      const response = await APIService.get(endpoint);
      console.log(`[RecentSimulationsAPI] Raw API response:`, response);

      const data = response.data || response;
      console.log(`[RecentSimulationsAPI] Extracted data:`, data);
      console.log(`[RecentSimulationsAPI] Data is array:`, Array.isArray(data));
      console.log(`[RecentSimulationsAPI] Data length:`, data?.length);

      if (Array.isArray(data) && data.length > 0) {
        console.log(`[RecentSimulationsAPI] Sample activity:`, data[0]);
      }

      return data;
    } catch (error) {
      console.error(
        "[RecentSimulationsAPI] Error fetching simulations for user:",
        error
      );
      throw error;
    }
  },
  /**
   * Get channel counts for a user
   * If userUuid is provided, uses admin endpoint (requires authorization)
   * If userUuid is not provided, uses self-access endpoint with authentication context
   * @param {string} [userUuid] - Optional UUID of user to fetch channel counts for (admin only)
   * @returns {Promise<Object>} Object with channel counts
   */
  getChannelCountsForUser: async (userUuid = null) => {
    try {
      const endpoint = userUuid
        ? `/api/v1/simulation/communication-simulations/user/${userUuid}/channel-counts`
        : `/api/v1/simulation/communication-simulations/user/channel-counts`;

      const response = await APIService.get(endpoint);
      return response.data || response;
    } catch (error) {
      console.error(
        "[RecentSimulationsAPI] Error fetching channel counts for user:",
        error
      );
      throw error;
    }
  },
};

/**
 * Helper function to get default subject based on channel type
 */
function getDefaultSubject(channelType) {
  switch (channelType.toLowerCase()) {
    case "email":
      return "Simulation Email";
    case "sms":
      return "Simulation SMS";
    case "voice":
      return "Simulation Voice Call";
    case "social-media":
      return "Simulation Social Media Message";
    default:
      return `Simulation ${channelType} Message`;
  }
}

/**
 * Helper function to extract message content based on channel type
 */
function getMessageContent(messageData) {
  return (
    messageData.content ||
    messageData.emailBody ||
    messageData.message ||
    messageData.messageContent ||
    messageData.messageScript ||
    "No content available"
  );
}

/**
 * Helper function to get recipient based on channel type
 */
function getRecipient(messageData, channelType) {
  switch (channelType.toLowerCase()) {
    case "email":
      return messageData.sendTo || messageData.recipient || "Unknown recipient";
    case "sms":
    case "voice":
      return (
        messageData.recipientPhone ||
        messageData.recipient ||
        "Unknown phone number"
      );
    case "social-media":
      const platform = messageData.platform || "Social Media";
      return (
        `${platform} user` ||
        messageData.recipient ||
        "Unknown social media user"
      );
    default:
      return messageData.recipient || "Unknown recipient";
  }
}

export default RecentSimulationsAPI;
